/**
 * Video Metadata Utilities
 *
 * Extracts metadata from video files using HTML5 Video API
 * and validates videos for Instagram Story requirements.
 */

const MAX_VIDEO_SIZE_MB = 100;
// Frontend performs basic format/size validation only
// Plan-specific duration limits are enforced on the backend by FeatureResolver
// This value is the absolute maximum for any plan (Enterprise: 180s)
const MAX_VIDEO_DURATION_SEC = 180;
const ALLOWED_VIDEO_EXTENSIONS = ['.mp4', '.mov'];

/**
 * Extract metadata from video file using HTML5 Video API.
 *
 * @param {File} file - Video file to analyze
 * @returns {Promise<Object>} Video metadata
 * @throws {Error} If video cannot be loaded
 */
export async function extractVideoMetadata(file) {
  return new Promise((resolve, reject) => {
    // Create video element
    const video = document.createElement('video');
    video.preload = 'metadata';

    // Create object URL for video file
    const objectUrl = URL.createObjectURL(file);

    // Handle metadata loaded event
    video.onloadedmetadata = () => {
      try {
        // Extract basic metadata
        const duration = Math.round(video.duration);
        const width = video.videoWidth;
        const height = video.videoHeight;

        // Detect if video has audio
        // Note: This is a basic check - may not work in all browsers
        const hasAudio = video.mozHasAudio ||
                        Boolean(video.webkitAudioDecodedByteCount) ||
                        Boolean(video.audioTracks && video.audioTracks.length);

        // Get video codec from file type
        const codec = getCodecFromMimeType(file.type);

        // Calculate FPS (approximation)
        // Note: Precise FPS detection requires more complex analysis
        const fps = 30.0; // Default assumption for most videos

        const metadata = {
          duration,
          resolution: `${width}x${height}`,
          width,
          height,
          fileSize: file.size,
          fileSizeMB: (file.size / (1024 * 1024)).toFixed(2),
          codec,
          fps,
          hasAudio: hasAudio || false, // Default to false if undetermined
          fileName: file.name,
          fileType: file.type,
        };

        // Clean up
        URL.revokeObjectURL(objectUrl);

        resolve(metadata);
      } catch (error) {
        URL.revokeObjectURL(objectUrl);
        reject(new Error(`Failed to extract video metadata: ${error.message}`));
      }
    };

    // Handle errors
    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load video file. The file may be corrupted or in an unsupported format.'));
    };

    // Load video
    video.src = objectUrl;
  });
}

/**
 * Get codec name from MIME type.
 *
 * @param {string} mimeType - Video MIME type
 * @returns {string} Codec name
 */
function getCodecFromMimeType(mimeType) {
  if (!mimeType) return 'Unknown';

  const lowerType = mimeType.toLowerCase();

  if (lowerType.includes('mp4') || lowerType.includes('h264')) {
    return 'H.264';
  } else if (lowerType.includes('hevc') || lowerType.includes('h265')) {
    return 'H.265';
  } else if (lowerType.includes('quicktime')) {
    return 'QuickTime';
  }

  return 'Unknown';
}

/**
 * Validate video file for Instagram Story requirements.
 *
 * @param {File} file - Video file to validate
 * @param {Object} metadata - Video metadata from extractVideoMetadata
 * @returns {Object} Validation result { isValid, error }
 * @description Error objects contain i18n keys for translation
 *   - error.key: i18n key (e.g., 'errors.upload.videoTooLong')
 *   - error.params: i18n parameters (e.g., { duration: 75 })
 *   - error.type: Legacy error type for fallback display
 */
export function validateVideo(file, metadata) {
  // Check file extension
  const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
  if (!ALLOWED_VIDEO_EXTENSIONS.includes(fileExtension)) {
    return {
      isValid: false,
      error: {
        type: 'INVALID_FORMAT',
        key: 'errors.upload.invalidFormat',
        title: 'Unsupported video format',
        message: `Please upload an MP4 or MOV file. The file '${file.name}' is not supported.`,
      },
    };
  }

  // Check file size
  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > MAX_VIDEO_SIZE_MB) {
    return {
      isValid: false,
      error: {
        type: 'FILE_TOO_LARGE',
        key: 'errors.upload.fileTooLarge',
        params: { max: MAX_VIDEO_SIZE_MB },
        title: 'Video file too large',
        message: `Please select a video under ${MAX_VIDEO_SIZE_MB}MB. Your video is ${fileSizeMB.toFixed(2)}MB.`,
      },
    };
  }

  // Check duration (basic sanity check - detailed limits checked by backend)
  // Plan-specific limits are enforced on backend by FeatureResolver
  if (metadata.duration > MAX_VIDEO_DURATION_SEC) {
    return {
      isValid: false,
      error: {
        type: 'DURATION_TOO_LONG',
        key: 'errors.upload.videoTooLong',
        params: { duration: metadata.duration },
        title: 'Video exceeds time limit',
        message: `Your video is too long (${metadata.duration}s). Please select a shorter video or upgrade your plan for longer video support.`,
      },
    };
  }

  // Check duration minimum (at least 1 second)
  if (metadata.duration < 1) {
    return {
      isValid: false,
      error: {
        type: 'DURATION_TOO_SHORT',
        key: 'errors.upload.videoDurationTooShort',
        title: 'Video too short',
        message: 'Video must be at least 1 second long.',
      },
    };
  }

  // All validations passed
  return {
    isValid: true,
    error: null,
  };
}

/**
 * Format duration in seconds to human-readable string.
 *
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration (e.g., "1:23" or "15s")
 */
export function formatDuration(seconds) {
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

/**
 * Format file size in bytes to human-readable string.
 *
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "25.4 MB")
 */
export function formatFileSize(bytes) {
  const mb = bytes / (1024 * 1024);

  if (mb < 1) {
    const kb = bytes / 1024;
    return `${kb.toFixed(1)} KB`;
  }

  return `${mb.toFixed(1)} MB`;
}

/**
 * Create preview thumbnail from video file.
 *
 * @param {File} file - Video file
 * @param {number} seekTo - Time in seconds to capture thumbnail (default: 0)
 * @returns {Promise<string>} Data URL of thumbnail image
 */
export async function createVideoThumbnail(file, seekTo = 0) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    video.preload = 'metadata';
    video.muted = true;

    const objectUrl = URL.createObjectURL(file);

    video.onloadedmetadata = () => {
      video.currentTime = seekTo;
    };

    video.onseeked = () => {
      try {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);

        URL.revokeObjectURL(objectUrl);
        resolve(thumbnailUrl);
      } catch (error) {
        URL.revokeObjectURL(objectUrl);
        reject(new Error(`Failed to create thumbnail: ${error.message}`));
      }
    };

    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load video for thumbnail generation.'));
    };

    video.src = objectUrl;
  });
}

export const VIDEO_CONSTRAINTS = {
  MAX_SIZE_MB: MAX_VIDEO_SIZE_MB,
  MAX_DURATION_SEC: MAX_VIDEO_DURATION_SEC,
  ALLOWED_EXTENSIONS: ALLOWED_VIDEO_EXTENSIONS,
};
